import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import {
  login as apiLogin, logout as apiLogout, refreshSession,
  setUnauthenticatedHandler, type Role, type Session,
} from '@/api/client';

interface AuthState {
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Replaces the session in place — used after a self-service password change, which returns a
   * fresh session (new access token, mustChangePassword cleared) without a full sign-in. */
  setSession: (session: Session) => void;
  /** Role gating, mirroring the @PreAuthorize rules on the server (spec §12). */
  can: (...roles: Role[]) => boolean;
  /**
   * Whether this session is platform staff rather than a vendor's or company's own merchant-scoped
   * user — gates admin-only tools (force-reconcile, manual resolve) that act across merchants and
   * aren't part of a vendor/company's own self-service surface, mirroring the server's
   * platform-staff-only rule on the rest of /api/admin/**.
   */
  isPlatformStaff: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // On a reload the access token is gone but the refresh cookie may not be.
    refreshSession().then(setSession).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => setSession(null));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setSession(await apiLogin(email, password));
  }, []);

  const signOut = useCallback(async () => {
    await apiLogout();
    setSession(null);
  }, []);

  const can = useCallback(
    (...roles: Role[]) => (session ? roles.includes(session.role) : false),
    [session],
  );

  const isPlatformStaff = !session?.merchantCode;

  const value = useMemo(
    () => ({ session, loading, signIn, signOut, setSession, can, isPlatformStaff }),
    [session, loading, signIn, signOut, can, isPlatformStaff],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/**
 * Whether this role sees unmasked targets (spec §12). The server masks regardless — this only
 * decides whether the console bothers to explain why a number is starred out.
 */
export function seesUnmaskedPii(role: Role | undefined): boolean {
  return role === 'ADMIN' || role === 'OPS' || role === 'SUPPORT';
}
